/**
 * 発表会2027 タイムテーブルの計算（画面用）。
 *
 * サーバーの slash_admin_api/myapp/services/recital2027_timetable.py と同じ規則で、
 * 時刻・番号・連続出演を求める。PDF・CSV はサーバー側の計算から作るので、
 * 規則を変えるときは両方（とテスト test_recital2027_timetable.py）を直すこと。
 *
 * - 番号は本番（1部・2部）の演目だけを部ごとに数える。MC などの枠は番号を消費しない。
 * - 連続出演の距離は、枠も1ブロックとして数える。作品の間に自動で入る転換は数えない。
 * - 講師は連続出演の警告に含めない（設定で切り替え可）。
 */

export const STAGES = ["part1", "part2", "genepro"];
export const MAIN_STAGES = ["part1", "part2"];
export const STAGE_LABELS = { part1: "1部", part2: "2部", genepro: "ゲネプロ" };
export const KIND_LABELS = {
  class: "レッスン",
  regional: "選抜",
  headz: "HEADZ",
  instructor_show: "イントラショー",
  special: "特別ナンバー",
};
export const PERSON_TYPE_LABELS = { customer: "会員", entrant: "外部", instructor: "講師" };
export const DURATION_SOURCE_LABELS = { music: "音源", manual: "手入力", default: "既定" };

const pad = (n) => String(n).padStart(2, "0");

export function fmtDur(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

export function fmtLong(sec) {
  let s = Math.round(sec || 0);
  const neg = s < 0;
  s = Math.abs(s);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${neg ? "−" : ""}${h ? `${h}:${pad(m)}` : m}:${pad(s % 60)}`;
}

export function fmtClock(sec) {
  if (sec == null) return "--:--";
  const s = Math.floor(sec);
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}`;
}

export function parseClock(v) {
  if (!v) return null;
  const m = String(v).match(/^(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 : null;
}

/** 「3:20」「200」「3分20秒」を秒に。空は null、読めなければ NaN。 */
export function parseDur(v) {
  const t = String(v ?? "")
    .trim()
    .replace(/：/g, ":")
    .replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0));
  if (t === "") return null;
  let m = t.match(/^(\d+):(\d{1,2})$/);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = t.match(/^(\d+)分(?:(\d+)秒?)?$/);
  if (m) return Number(m[1]) * 60 + Number(m[2] || 0);
  m = t.match(/^(\d+)秒?$/);
  if (m) return Number(m[1]);
  return NaN;
}

/** 演目の尺：提出された音源の長さ ＞ 画面で入れた尺 ＞ 既定（単独レッスン／それ以外）。 */
export function durationOf(work, settings) {
  if (work.music_sec != null) return { sec: Math.round(work.music_sec), source: "music" };
  if (work.planned_duration_sec != null) return { sec: work.planned_duration_sec, source: "manual" };
  const single = work.kind === "class" && !work.is_joint;
  return { sec: single ? settings.default_lesson_sec : settings.default_group_sec, source: "default" };
}

/**
 * 出番の尺。ゲネプロの作品の行にゲネプロ用の尺が入っていればそれを使い、無ければ本番と同じ尺。
 * ゲネプロはリハーサル・場当たりを兼ねて長く取ることがあるので、音源が提出済みでも変えられる。
 * base は本番の尺（durationOf の結果）。
 */
export function slotDurationOf(stage, slot, work, settings) {
  const base = work ? durationOf(work, settings) : { sec: 0, source: "default" };
  if (stage === "genepro" && slot.duration_sec != null) return { sec: slot.duration_sec, source: "genepro", base };
  return { ...base, base };
}

/**
 * slotsByStage: {stage: [slot]}（並び順どおり）
 * works: {id: work}（performers 付き）
 * settings: サーバーの設定（開始時刻は "HH:MM"）
 */
export function computeTimetable(slotsByStage, works, settings) {
  const numbers = {};
  MAIN_STAGES.forEach((stage) => {
    let n = 0;
    (slotsByStage[stage] || []).forEach((s) => {
      if (s.slot_type !== "work") return;
      n += 1;
      numbers[s.id] = `${STAGE_LABELS[stage]}-${n}`;
    });
  });
  const out = { numbers };
  STAGES.forEach((stage) => {
    out[stage] = computeStage(stage, slotsByStage[stage] || [], works, settings, numbers);
  });
  return out;
}

function computeStage(stage, slots, works, settings, numbers) {
  const start = parseClock(settings[`${stage}_start_time`]);
  const defaultCo = settings.default_changeover_sec || 0;
  const gapMax = settings.warn_gap_max || 5;
  const includeInstructor = !!settings.warn_include_instructor;
  const rows = [];
  const bySid = {};
  let t = 0;
  let nWork = 0;
  let nFree = 0;
  slots.forEach((s, i) => {
    const next = slots[i + 1];
    const isWork = s.slot_type === "work";
    const work = isWork ? works[s.work_id] : null;
    const dur = isWork ? slotDurationOf(stage, s, work, settings).sec : s.duration_sec || 0;
    let co = null;
    if (isWork && next && next.slot_type === "work") {
      co = s.changeover_sec_override != null ? s.changeover_sec_override : defaultCo;
    }
    if (isWork) nWork += 1;
    else nFree += 1;
    const row = {
      sid: s.id,
      pos: i + 1,
      offset: t,
      clock: start == null ? null : start + t,
      dur,
      co,
      no: isWork && stage !== "genepro" ? numbers[s.id] || null : null,
      ref: isWork && stage === "genepro" ? numbers[s.main_slot_id] || null : null,
    };
    rows.push(row);
    bySid[s.id] = row;
    t += dur + (co || 0);
  });

  const occ = {};
  rows.forEach((r, i) => {
    const s = slots[i];
    if (s.slot_type !== "work") return;
    ((works[s.work_id] || {}).performers || []).forEach((p) => {
      if (p.type === "instructor" && !includeInstructor) return;
      (occ[p.key] = occ[p.key] || []).push({ pos: r.pos, sid: s.id, name: p.name });
    });
  });
  const pairs = [];
  const slotMin = {};
  const anno = {};
  const setA = (sid, key, dir, g) => {
    const a = ((anno[sid] = anno[sid] || {})[key] = anno[sid][key] || {});
    a[dir] = Math.min(a[dir] ?? 99, g);
  };
  Object.entries(occ).forEach(([key, arr]) => {
    for (let j = 1; j < arr.length; j += 1) {
      const a = arr[j - 1];
      const b = arr[j];
      const g = b.pos - a.pos;
      if (g > gapMax) continue;
      pairs.push({ key, name: a.name, a: a.sid, b: b.sid, gap: g, stage });
      slotMin[a.sid] = Math.min(slotMin[a.sid] ?? 99, g);
      slotMin[b.sid] = Math.min(slotMin[b.sid] ?? 99, g);
      setA(a.sid, key, "next", g);
      setA(b.sid, key, "prev", g);
    }
  });
  pairs.sort((x, y) => x.gap - y.gap || bySid[x.a].pos - bySid[y.a].pos);
  return { rows, bySid, total: t, start, end: start == null ? null : start + t, pairs, slotMin, anno, nWork, nFree };
}

/**
 * ドラッグ中のプレビュー：演目を items の index の位置に置いたときの最短の距離（ブロック数）。
 * items は置いたあとの並び（枠も含む）。連続出演がなければ null。
 */
export function minGapAt(items, index, workId, works, settings) {
  const gapMax = settings.warn_gap_max || 5;
  const includeInstructor = !!settings.warn_include_instructor;
  const keys = new Set(
    ((works[workId] || {}).performers || [])
      .filter((p) => includeInstructor || p.type !== "instructor")
      .map((p) => p.key)
  );
  if (!keys.size) return null;
  let best = null;
  items.forEach((it, j) => {
    if (j === index || it.slot_type !== "work") return;
    const d = Math.abs(j - index);
    if (d > gapMax || (best != null && d >= best)) return;
    const hit = ((works[it.work_id] || {}).performers || []).some(
      (p) => (includeInstructor || p.type !== "instructor") && keys.has(p.key)
    );
    if (hit) best = d;
  });
  return best;
}

/** 行の見出し。レッスンは「店舗／イントラ」、それ以外は演目名。 */
export function isLessonWork(w) {
  return w.kind === "class" && w.lessons.length > 0;
}

export function subOf(w) {
  if (isLessonWork(w)) return w.lessons.map((l) => `${l.name} ${l.count}`).join(" ＋ ");
  if (w.kind === "instructor_show") return "イントラショー · 全講師";
  return `${KIND_LABELS[w.kind] || w.kind}${w.instructors.length ? ` · 担当 ${w.instructors.join("・")}` : ""}`;
}
